import { CompleteStylistFragment, Maybe } from 'drycut-graphql';
import { PropsWithChildren, createContext, useContext, useRef, useState } from 'react';
import { useBoolean } from 'usehooks-ts';

type StylistCardContextType = {
  stylist: Maybe<CompleteStylistFragment>;
  actionsOpen: boolean;
  pauseDialogOpen: boolean;
  toggleActions: () => void;
  togglePauseDialog: () => void;
  setActionLoading: (loading: boolean) => void;
  actionLoading: boolean;
};

const StylistCardContext = createContext<StylistCardContextType>({
  stylist: null,
  actionsOpen: false,
  pauseDialogOpen: false,
  toggleActions: () => false,
  togglePauseDialog: () => false,
  setActionLoading: () => false,
  actionLoading: false,
});

type ProviderProps = PropsWithChildren<{
  stylist: CompleteStylistFragment;
}>;

const MENU_TIMEOUT = 10000;

export function StylistCardProvider({ children, stylist }: ProviderProps): React.ReactElement {
  const [actionLoading, setActionLoading_real] = useState<boolean>(false);
  const { value: actionsOpen, toggle: toggleActionsFn } = useBoolean(false);
  const { value: pauseDialogOpen, toggle: togglePauseDialogFn } = useBoolean(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const toggleActions = () => {
    // We are opening the actions
    if (!actionsOpen) {
      timer.current = setTimeout(() => {
        toggleActionsFn();
      }, MENU_TIMEOUT);
    } else {
      // We are closing the actions, we clear the timeout to prevent the menu from opening after
      // we closed it
      clearTimeout(timer.current);
    }

    toggleActionsFn();
  };

  const togglePauseDialog = () => {
    // We are opening the pause dialog
    if (!pauseDialogOpen) {
      clearTimeout(timer.current);
    } else {
      toggleActionsFn();
    }
    togglePauseDialogFn();
  };

  const setActionLoading = (loading: boolean) => {
    setActionLoading_real(loading);
  };

  return (
    <StylistCardContext.Provider
      value={{
        stylist,
        actionsOpen,
        pauseDialogOpen,
        toggleActions,
        togglePauseDialog,
        actionLoading,
        setActionLoading,
      }}
    >
      {children}
    </StylistCardContext.Provider>
  );
}

export const useStylistCardContext = () => {
  return useContext(StylistCardContext);
};
